import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Users, Gift } from "lucide-react";
import dayjs from "dayjs";

const EuropeCampaignPerks = ({
  setOpenLuckyDrawModal,
  setLuckyDrawLoading,
  campaign,
  onJoinCampaign,
  joinedCampaign,
  isAuthenticated,
  community,
}) => {
  const { t } = useTranslation();
  const isTW = community === "tw";

  const now = dayjs();

  // Campaign period checks
  const campaignNotStarted = campaign?.promo_start_date_inclusive
    ? now.isBefore(dayjs(campaign.promo_start_date_inclusive))
    : false;

  const campaignActive =
    campaign?.promo_start_date_inclusive && campaign?.promo_end_date_inclusive
      ? now.isAfter(dayjs(campaign.promo_start_date_inclusive)) &&
        now.isBefore(dayjs(campaign.promo_end_date_inclusive))
      : false;

  const campaignEnded = campaign?.promo_end_date_inclusive
    ? now.isAfter(dayjs(campaign.promo_end_date_inclusive))
    : false;

  // Voucher period checks
  const voucherNotStarted = campaign?.voucher_validity_start_date
    ? now.isBefore(dayjs(campaign.voucher_validity_start_date))
    : false;

  const voucherActive =
    campaign?.voucher_validity_start_date && campaign?.voucher_validity_end_date
      ? now.isAfter(dayjs(campaign.voucher_validity_start_date)) &&
        now.isBefore(dayjs(campaign.voucher_validity_end_date))
      : false;

  const voucherExpired = campaign?.voucher_validity_end_date
    ? now.isAfter(dayjs(campaign.voucher_validity_end_date))
    : false;

  // Determine button state and text
  const getButtonState = () => {
    // Not authenticated
    if (!isAuthenticated) {
      return {
        disabled: false,
        text: t("campaign.europe.perks.lucky_draw.button_text"),
        description: t("campaign.europe.perks.lucky_draw.sign_in_required"),
      };
    }

    // Campaign not started
    if (campaignNotStarted) {
      return {
        disabled: true,
        text: "Coming Soon",
        description: t("campaign.europe.perks.lucky_draw.not_started"),
      };
    }

    // Campaign active
    if (campaignActive) {
      if (joinedCampaign) {
        return {
          disabled: true,
          text: "Already Joined!",
          description: t("campaign.europe.perks.lucky_draw.already_joined"),
        };
      }
      return {
        disabled: false,
        text: t("campaign.europe.perks.lucky_draw.button_text"),
        description: t("campaign.europe.perks.lucky_draw.description"),
      };
    }

    // Campaign ended - waiting for voucher validity
    if (campaignEnded && voucherNotStarted) {
      if (joinedCampaign) {
        const announceDate = campaign?.voucher_validity_start_date
          ? dayjs(campaign.voucher_validity_start_date).format("D MMM")
          : "17 Mar";
        return {
          disabled: true,
          text: "Awaiting Results",
          description: t("campaign.europe.perks.lucky_draw.awaiting_announcement", {
            date: announceDate,
          }),
        };
      }
      return {
        disabled: true,
        text: "Campaign Ended",
        description: t("campaign.europe.perks.lucky_draw.campaign_ended"),
      };
    }

    // Voucher period active
    if (voucherActive) {
      if (joinedCampaign) {
        return {
          disabled: true,
          text: "Check Your App!",
          description: t("campaign.europe.perks.lucky_draw.voucher_period_active"),
        };
      }
      return {
        disabled: true,
        text: "Campaign Ended",
        description: t("campaign.europe.perks.lucky_draw.campaign_ended"),
      };
    }

    // Voucher expired
    if (voucherExpired) {
      return {
        disabled: true,
        text: "Campaign Ended",
        description: t("campaign.europe.perks.lucky_draw.all_ended"),
      };
    }

    // Default fallback
    return {
      disabled: true,
      text: "Unavailable",
      description: t("campaign.europe.perks.lucky_draw.description"),
    };
  };

  const buttonState = getButtonState();

  const handleLuckyDraw = () => {
    if (buttonState.disabled) {
      return;
    }

    // For non-authenticated users, the button should trigger sign-in
    // (handled by parent component)
    if (onJoinCampaign) {
      onJoinCampaign();
    }
  };

  const groupDiscountTiers = [
    {
      id: 1,
      minPeople: isTW ? 4 : 3,
      discount: "5%",
      label: t("campaign.europe.perks.group_discount.tier_3"),
    },
    {
      id: 2,
      minPeople: isTW ? 6 : 5,
      discount: "15%",
      label: t("campaign.europe.perks.group_discount.tier_5"),
    },
  ];

  // TW: Tiered "Overcoming Regrets" Pass pricing; SG: Lucky draw vouchers
  const perk1Items = isTW
    ? [
        { id: 1, amount: "NT$1,588", subtitle: t("campaign.europe.perks.lucky_draw.slots", { count: 48 }), color: "tw-from-green-400 tw-to-teal-500" },
        { id: 2, amount: "NT$3,688", subtitle: t("campaign.europe.perks.lucky_draw.slots", { count: 18 }), color: "tw-from-blue-400 tw-to-indigo-500" },
        { id: 3, amount: "NT$6,188", subtitle: t("campaign.europe.perks.lucky_draw.slots", { count: 3 }), color: "tw-from-purple-400 tw-to-pink-500" },
        { id: 4, amount: "NT$15,088", subtitle: t("campaign.europe.perks.lucky_draw.slots", { count: 1 }), color: "tw-from-yellow-400 tw-to-orange-500" },
      ]
    : [
        { id: 1, amount: "$666", subtitle: t("campaign.europe.perks.lucky_draw.voucher_label"), color: "tw-from-yellow-400 tw-to-orange-500" },
        { id: 2, amount: "$366", subtitle: t("campaign.europe.perks.lucky_draw.voucher_label"), color: "tw-from-purple-400 tw-to-pink-500" },
        { id: 3, amount: "$166", subtitle: t("campaign.europe.perks.lucky_draw.voucher_label"), color: "tw-from-blue-400 tw-to-indigo-500" },
        { id: 4, amount: "$66", subtitle: t("campaign.europe.perks.lucky_draw.voucher_label"), color: "tw-from-green-400 tw-to-teal-500" },
      ];

  return (
    <div className="tw-mb-16 tw-space-y-8">
      {/* Perk 1: Lucky Draw Vouchers */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="tw-relative tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-purple-500 tw-to-pink-600 tw-p-8 tw-shadow-xl"
      >
        <div className="tw-bg-grid-white/[0.1] tw-absolute tw-inset-0 tw-opacity-20" />

        <div className="tw-relative tw-mb-8 tw-flex tw-flex-col tw-items-center tw-text-center">
          <div className="tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/20 tw-backdrop-blur-sm">
            <Gift className="tw-h-8 tw-w-8 tw-text-white" />
          </div>
          <h2 className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-white md:tw-text-3xl">
            {t("campaign.europe.perks.lucky_draw.title")}
          </h2>
          <p className="tw-max-w-2xl tw-text-white/80">
            {t("campaign.europe.perks.lucky_draw.subtitle")}
          </p>
        </div>

        {/* Display Perk 1 Items */}
        <div className="tw-relative tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-4">
          {perk1Items.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className={`tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-bg-gradient-to-br ${item.color} tw-p-6 tw-shadow-lg`}
            >
              {/* Ticket notch top */}
              <div className="tw-absolute tw-left-0 tw-top-0 tw-flex tw-h-4 tw-w-full tw-justify-between tw-px-2">
                <div
                  className={`tw--ml-2 tw--mt-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br ${item.color}`}
                />
                <div
                  className={`tw--mr-2 tw--mt-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br ${item.color}`}
                />
              </div>

              <div className="tw-mt-2 tw-text-center">
                <span className="tw-text-3xl tw-font-bold tw-text-white">{item.amount}</span>
                <p className="tw-mt-1 tw-text-sm tw-text-white/90">{item.subtitle}</p>
              </div>

              {/* Ticket notch bottom */}
              <div className="tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-h-4 tw-w-full tw-justify-between tw-px-2">
                <div
                  className={`tw--mb-2 tw--ml-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br ${item.color}`}
                />
                <div
                  className={`tw--mb-2 tw--mr-2 tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-br ${item.color}`}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Lucky Draw Button - Below Vouchers */}
        <div className="tw-relative tw-flex tw-flex-col tw-items-center">
          <motion.button
            onClick={handleLuckyDraw}
            disabled={buttonState.disabled}
            whileHover={!buttonState.disabled ? { scale: 1.05 } : {}}
            whileTap={!buttonState.disabled ? { scale: 0.95 } : {}}
            className={`tw-rounded-full tw-px-8 tw-py-4 tw-text-lg tw-font-bold tw-shadow-lg tw-transition-all ${
              buttonState.disabled
                ? "tw-cursor-not-allowed tw-bg-gray-300 tw-text-gray-500"
                : joinedCampaign
                  ? "tw-cursor-not-allowed tw-bg-green-500 tw-text-white"
                  : "tw-bg-white tw-text-purple-600 hover:tw-shadow-xl"
            }`}
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              <Gift className="tw-h-5 tw-w-5" />
              <span>{buttonState.text}</span>
            </div>
          </motion.button>
        </div>

        <div className="tw-relative tw-mt-6 tw-text-center">
          <p className="tw-text-sm tw-text-white/90">{buttonState.description}</p>
        </div>
      </motion.div>

      {/* Perk 2: Group Booking Discounts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="tw-relative tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-blue-500 tw-to-cyan-600 tw-p-8 tw-shadow-xl"
      >
        <div className="tw-bg-grid-white/[0.1] tw-absolute tw-inset-0 tw-opacity-20" />

        <div className="tw-relative tw-mb-8 tw-flex tw-flex-col tw-items-center tw-text-center">
          <div className="tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/20 tw-backdrop-blur-sm">
            <Users className="tw-h-8 tw-w-8 tw-text-white" />
          </div>
          <h2 className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-white md:tw-text-3xl">
            {t("campaign.europe.perks.group_discount.title")}
          </h2>
          <p className="tw-max-w-2xl tw-text-white/80">
            {t("campaign.europe.perks.group_discount.subtitle")}
          </p>
          <p className="tw-mt-2 tw-text-sm tw-font-medium tw-text-yellow-200">
            {t("campaign.europe.perks.group_discount.validity_note")}
          </p>
        </div>

        <div className="tw-relative tw-mb-6 tw-rounded-xl tw-bg-white/10 tw-p-4 tw-text-sm tw-text-white/90">
          <p>{t("campaign.europe.perks.group_discount.note")}</p>
        </div>

        <div className="tw-relative tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2">
          {groupDiscountTiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border-2 tw-border-white/30 tw-bg-white/10 tw-p-6 tw-backdrop-blur-sm"
            >
              <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-space-x-2">
                  <Users className="tw-h-6 tw-w-6 tw-text-white" />
                  <span className="tw-text-lg tw-font-semibold tw-text-white">
                    {t("campaign.europe.perks.group_discount.people_count", { count: tier.minPeople })}
                  </span>
                </div>
                <div className="tw-rounded-full tw-bg-white/20 tw-px-4 tw-py-2">
                  <span className="tw-text-2xl tw-font-bold tw-text-white">{tier.discount}</span>
                  <span className="tw-ml-1 tw-text-sm tw-text-white/80">{t("campaign.europe.perks.group_discount.off")}</span>
                </div>
              </div>
              <p className="tw-text-sm tw-text-white/90">{tier.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default EuropeCampaignPerks;
